import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Container } from '../components/common/Container';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { Textarea } from '../components/common/Textarea';
import { Button } from '../components/common/Button';
import { EnvelopeIcon, PhoneIcon, ClockIcon } from '@heroicons/react/24/outline';

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  company: z.string().min(2, 'Company name is required'),
  propertyType: z.string().min(1, 'Please select a property type'),
  propertyCount: z.string().optional(),
  message: z.string().min(10, 'Please provide more details (at least 10 characters)')
});

type ContactFormData = z.infer<typeof contactSchema>;

export const Contact: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema)
  });
  
  const onSubmit = async (data: ContactFormData) => {
    setSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log('Form data:', data);
    setSubmitted(true);
    setSubmitting(false);
    reset();
    
    // Reset success message after 5 seconds
    setTimeout(() => setSubmitted(false), 5000);
  };
  
  const propertyTypeOptions = [
    { value: '', label: 'Select property type...' },
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'condominium', label: 'Condominium' },
    { value: 'mixed', label: 'Mixed Portfolio' }
  ];
  
  return (
    <>
      {/* Hero Section */}
      <section className="bg-brand-blue py-20">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-brand-blue-100">
              Have questions? Want to schedule a demo? We're here to help.
            </p>
          </div>
        </Container>
      </section>
      
      {/* Contact Content */}
      <section className="py-24 bg-white">
        <Container>
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-brand-blue mb-6">
                Let's Talk About Your Needs
              </h2>
              <p className="text-lg text-brand-white-700 mb-8">
                Whether you're a boutique firm handling hundreds of properties or
                an enterprise organization managing thousands, we'd love to show
                you how Appeal Advantage can streamline your workflow.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-brand-blue rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <EnvelopeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-blue mb-1">Email Us</h3>
                    <p className="text-brand-white-700">info@appealadvantage.com</p>
                    <p className="text-sm text-brand-white-600 mt-1">We typically respond within 24 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-brand-blue rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <PhoneIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-blue mb-1">Call Us</h3>
                    <p className="text-brand-white-700">Schedule a call</p>
                    <p className="text-sm text-brand-white-600 mt-1">Available Monday - Friday, 9am - 5pm EST</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-brand-blue rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <ClockIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-blue mb-1">Response Time</h3>
                    <p className="text-brand-white-700">Within 24 hours</p>
                    <p className="text-sm text-brand-white-600 mt-1">For urgent inquiries, please mention in your message</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-12 p-6 bg-brand-blue-50 rounded-lg">
                <h3 className="font-bold text-brand-blue mb-3">
                  Prefer a Live Demo?
                </h3>
                <p className="text-brand-white-700 mb-4">
                  See Appeal Advantage in action with a personalized walkthrough
                  tailored to your specific needs and property types.
                </p>
                <a 
                  href="https://calendly.com/appealadvantage/appeal-advantage-platform-demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="secondary" size="md" className="w-full">
                    Schedule a Demo
                  </Button>
                </a>
              </div>
            </div>
            
            {/* Contact Form */}
            <div>
              <div className="bg-white border border-gray-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-brand-blue mb-6">
                  Send Us a Message
                </h3>
                
                {submitted && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 font-medium">
                      Thank you! We've received your message and will respond within 24 hours.
                    </p>
                  </div>
                )}
                
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <Input
                    label="Full Name *"
                    {...register('name')}
                    error={errors.name?.message}
                    placeholder="John Smith"
                  />
                  
                  <Input
                    label="Email Address *"
                    type="email"
                    {...register('email')}
                    error={errors.email?.message}
                    placeholder="john@company.com"
                  />
                  
                  <Input
                    label="Company Name *"
                    {...register('company')}
                    error={errors.company?.message}
                    placeholder="Your Company LLC"
                  />
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <Select
                      label="Property Type *"
                      {...register('propertyType')}
                      error={errors.propertyType?.message}
                      options={propertyTypeOptions}
                    />
                    
                    <Input
                      label="Number of Properties"
                      {...register('propertyCount')}
                      placeholder="e.g., 50-100"
                    />
                  </div>
                  
                  <Textarea
                    label="Message *"
                    {...register('message')}
                    error={errors.message?.message}
                    rows={5}
                    placeholder="Tell us about your needs, questions, or what you'd like to learn more about..."
                  />
                  
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={submitting}
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                  
                  <p className="text-sm text-brand-white-600 text-center">
                    We'll respond within 24 hours
                  </p>
                </form>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};

